import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { ImpactStyle } from '@capacitor/haptics';
import { CardModal, Session } from '@mindly/ui-components';
import { userSelector } from '../../redux/user/selector';
import {
  selectSessionForReview,
  selectTrialSessionReviewModalIsOpen,
} from '../../redux/consultationReviews/consultationsReviews.selector';
import {
  setSessionForReview,
  setTrialSessionReviewModalIsOpen,
} from '../../redux/consultationReviews/consultationsReviews.reducer';
import { createConsultationReview } from '../../redux/consultationReviews/consultationsReviews.thunk';
import { updateConsultationBy } from '../../redux/consultations/reducer';
import { addPromoCode } from '../../redux/promoCodes/promoCodes.reducer';
import { selectUnUsedPromoCode } from '../../redux/promoCodes/promoCodes.selectors';
import { lastActiveSubscriptionSelector } from '../../redux/subscriptions/selector';
import { useRemoteConfig } from '../../hooks/useRemoteConfig/useRemoteConfig';
import { getConsultationByID } from '../../api/consultation';
import { TranslatedReviewTrialSessionFeature } from '../../translated-components';
import { SubscriptionContinueTherapyFeature } from '../SubscriptionContinueTherapyFeature';
import {
  mergeSession,
  saveTrialSessionReviewPromoCode,
  saveTrialSessionReviewContinueTherapy,
  removeTrialSessionReviewPromoCode,
  removeTrialSessionReviewContinueTherapy,
} from '../../shared/utils/manageTrialSessionReview';
import { useSessionRatingAnalytics } from '../../shared';
import PromocodeFeature from '../PromocodeFeature/PromocodeFeature';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';

const NORMAL_REVIEWS_RATING = [4, 5];

const ReviewTrialSessionFeature: React.FC = () => {
  const dispatch = useDispatch();
  const { getConfigValue } = useRemoteConfig();

  const isOpen = useSelector(selectTrialSessionReviewModalIsOpen);
  const client = useSelector(userSelector);
  const session: Session = useSelector(selectSessionForReview) as Session;
  const currentPromoCode = useSelector(selectUnUsedPromoCode);
  const specialistSubscription = useSelector(lastActiveSubscriptionSelector(session?.psychologist_id));
  const [isContinueTherapyOpened, setIsContinueTherapyOpened] = useState(false);
  const [isPromoCodeModalOpen, setIsPromoCodeModalOpen] = useState(false);
  const [promoCodePercentOff, setPromoCodePercentOff] = useState(0);

  const ratingAnalyticsTracker = useSessionRatingAnalytics(session);

  useEffect(() => {
    getConfigValue('second_trial_session_percent_off_promocode').then((value) => {
      setPromoCodePercentOff(+value);
    });
  }, []);

  useEffect(() => {
    if (isPromoCodeModalOpen) {
      saveTrialSessionReviewPromoCode(true);
    } else {
      removeTrialSessionReviewPromoCode();
    }
  }, [isPromoCodeModalOpen]);

  useEffect(() => {
    if (isContinueTherapyOpened) {
      saveTrialSessionReviewContinueTherapy(true);
    } else {
      removeTrialSessionReviewContinueTherapy();
    }
  }, [isContinueTherapyOpened]);

  if (!session || !session.payment_intent_id) {
    return null;
  }

  const handleClose = () => {
    if (session?.id) {
      mergeSession(session.id, {
        hideForever: true,
        isOpen: false,
        sessionStartTimestamp: DateTime.local().toMillis(),
        userId: client?.id ?? '',
      });
    }
    dispatch(setSessionForReview(null));
    dispatch(setTrialSessionReviewModalIsOpen(false));
  };

  const handleSubmit = async ({ rate, reasons, comment }: { rate: number; reasons: string[]; comment?: string }) => {
    const isGoodReview = NORMAL_REVIEWS_RATING.includes(rate);

    const review = await dispatch(
      createConsultationReview({
        text_of_review: comment ?? '',
        stars_count: rate,
        reasons: reasons ?? [],
        client_id: client?.id ?? '',
        client_name: `${client?.display_name} ${client?.last_name}`,
        client_avatar: client?.avatar ?? '',
        created_at: DateTime.utc().toMillis(),
        specialist_id: session?.psychologist_id ?? '',
        consultation_id: session?.id ?? '',
        ...(!isGoodReview && { promo_code_percent_off: +promoCodePercentOff }),
      })
    ).unwrap();

    const updatedSession = await getConsultationByID(session?.id ?? '');
    if (updatedSession.session_review) {
      dispatch(
        updateConsultationBy({
          field: 'id',
          value: session?.id ?? '',
          data: {
            session_review: updatedSession.session_review,
          },
        })
      );
    }

    if (review.promo_code === null) {
      if (!specialistSubscription) {
        setIsContinueTherapyOpened(true);
        ratingAnalyticsTracker.trackRatingNextSessionScreenViewed();
      }
    } else {
      ratingAnalyticsTracker.trackRatingDiscountOffered(review.promo_code?.percent_off, review.promo_code?.id);
      setIsPromoCodeModalOpen(true);
      dispatch(addPromoCode(review.promo_code));
      handleClose();
    }
  };

  return (
    <>
      <TranslatedReviewTrialSessionFeature
        isOpen={isOpen && !isContinueTherapyOpened}
        specialistName={session?.psychologist_name ?? ''}
        specialistAvatar={session?.psychologist_avatar ?? ''}
        onModalAppeared={() => ratingAnalyticsTracker.trackRatingScreenViewed()}
        onStarsClick={(value) => {
          ratingAnalyticsTracker.trackRatingButtonClicked(value);
          isomorficHapticsImpact({ style: ImpactStyle.Light });
        }}
        onReasonClick={(value, reasons) => ratingAnalyticsTracker.trackReasonTagSelected(value, reasons)}
        onCommentBlur={(value, reasons, comment) => ratingAnalyticsTracker.trackCommentBlur(value, reasons, comment)}
        onSubmit={handleSubmit}
        onClose={handleClose}
        onContinueClick={() => {
          isomorficHapticsImpact({ style: ImpactStyle.Light });
        }}
        onCloseClick={() => {
          if (isContinueTherapyOpened) {
            ratingAnalyticsTracker.trackRatingNextSessionCloseClicked();
          } else {
            ratingAnalyticsTracker.trackRatingButtonClosedClicked();
          }
        }}
      />
      <CardModal
        isOpen={isContinueTherapyOpened}
        isShowHandle={false}
        onCloseClick={() => {
          ratingAnalyticsTracker.trackRatingNextSessionCloseClicked();
          setIsContinueTherapyOpened(false);
        }}
      >
        <SubscriptionContinueTherapyFeature
          specialistId={session?.psychologist_id ?? ''}
          specialistName={session?.psychologist_name ?? ''}
          onContinueTherapy={() => {
            ratingAnalyticsTracker.trackRatingNextSessionContinueClicked();
          }}
          onClose={() => {
            handleClose();
            setIsContinueTherapyOpened(false);
          }}
        />
      </CardModal>
      <CardModal isShowHandle={false} isOpen={isPromoCodeModalOpen} onCloseClick={() => setIsPromoCodeModalOpen(false)}>
        <PromocodeFeature
          discount={promoCodePercentOff}
          expiredDate={currentPromoCode?.end_date?._seconds}
          onClose={() => setIsPromoCodeModalOpen(false)}
        />
      </CardModal>
    </>
  );
};

export default ReviewTrialSessionFeature;
